import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Settings, Bell, Mail, MessageSquare, Shield } from "lucide-react";
import { useNotificationPreferences } from "@/hooks/useProfileData";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth-provider";
import { Skeleton } from "@/components/ui/skeleton";

export const SettingsTab = () => {
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();
  const { data: preferences, isLoading } = useNotificationPreferences();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handlePreferenceChange = async (key: string, value: boolean) => {
    if (!user?.id) return;

    setIsSaving(true);
    try {
      // Get profile ID
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Update or insert notification preferences
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: profile.id,
          [key]: value,
        } as never);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      
      toast({
        title: "Preferences updated",
        description: "Your notification preferences have been saved.",
      });
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Notification Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-6 w-11" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const notificationSettings = [
    {
      key: 'email_notifications',
      title: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: Mail,
      value: preferences?.email_notifications ?? true,
    },
    {
      key: 'sms_notifications',
      title: 'SMS Notifications',
      description: 'Receive notifications via SMS',
      icon: MessageSquare,
      value: preferences?.sms_notifications ?? true,
    },
    {
      key: 'booking_updates',
      title: 'Booking Updates',
      description: 'Get notified about booking status changes',
      icon: Bell,
      value: preferences?.booking_updates ?? true,
    },
    {
      key: 'marketing_emails',
      title: 'Marketing Emails',
      description: 'Receive promotional emails and newsletters',
      icon: Mail,
      value: preferences?.marketing_emails ?? false,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {notificationSettings.map((setting, index) => (
            <div key={setting.key}>
              <div className="flex items-center justify-between">
                <div className="flex items-start space-x-3">
                  <setting.icon className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <Label htmlFor={setting.key} className="text-base font-medium">
                      {setting.title}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {setting.description}
                    </p>
                  </div>
                </div>
                <Switch
                  id={setting.key}
                  checked={setting.value}
                  onCheckedChange={(checked) => handlePreferenceChange(setting.key, checked)}
                  disabled={isSaving}
                />
              </div>
              {index < notificationSettings.length - 1 && <Separator className="mt-6" />}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Privacy Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Privacy & Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-base font-medium">Profile Visibility</Label>
              <p className="text-sm text-muted-foreground">
                Your profile is currently private and only visible to you
              </p>
            </div>
            <Switch checked={false} disabled />
          </div>
          
          <Separator />
          
          <div className="space-y-3">
            <Label className="text-base font-medium">Data Management</Label>
            <div className="space-y-2">
              <Button variant="outline" className="w-full md:w-auto">
                Export My Data
              </Button>
              <Button variant="outline" className="w-full md:w-auto text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground">
                Delete Account
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            System Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-base font-medium">Dark Mode</Label>
              <p className="text-sm text-muted-foreground">
                Follow system theme settings
              </p>
            </div>
            <Switch checked={false} disabled />
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-base font-medium">Language</Label>
              <p className="text-sm text-muted-foreground">
                English (US)
              </p>
            </div>
            <Button variant="outline" size="sm" disabled>
              Change
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};